import { useState } from 'react';

import type { AdminStore } from '@/client/types/stores';
import type { StoreRefundRequest, StoreReturnRequest } from '@/client/types/stores/parity';
import { formatMoney } from '@/client/types/payment';
import {
  useStoreRefundRequestsQuery,
  useStoreReturnRequestsQuery,
} from '@/client/api/stores/parity.query';
import {
  useMarkStoreReturnReceived,
  useResolveStoreRefundRequest,
  useResolveStoreReturnRequest,
} from '@/client/api/stores/parity.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { cn } from '@/lib/utils';

import { Pager, Pill, SectionCard, label, shortDate, statusTone, ListSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';

const LIMIT = 20;
const STATUSES = { refunds: ['pending', 'approved', 'rejected', 'processed'], returns: ['pending', 'approved', 'rejected', 'received', 'refunded'] };

type Pending =
  | { kind: 'refund'; row: StoreRefundRequest; action: 'approve' | 'reject' }
  | { kind: 'return'; row: StoreReturnRequest; action: 'approve' | 'reject' }
  | { kind: 'received'; row: StoreReturnRequest; condition: 'resellable' | 'damaged' };

const buyer = (r: { buyer_username?: string | null; uid_buyer: number | null }) =>
  r.buyer_username ? `@${r.buyer_username}` : r.uid_buyer ? `User #${r.uid_buyer}` : '—';

const StatusFilter: React.FC<{ options: string[]; value: string; onChange: (v: string) => void }> = ({ options, value, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs" aria-label="Filter by status">
    <option value="">All</option>
    {options.map((s) => <option key={s} value={s}>{label(s)}</option>)}
  </select>
);

/**
 * The store's refund and return queue — the same decisions the seller makes in
 * Store Manager (`admin.store.orders`). Approving a refund debits the store
 * wallet if the seller turned auto-refund on; the gateway refund itself is
 * issued from Payments.
 */
export const RefundsTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [view, setView] = useState<'refunds' | 'returns'>('refunds');
  const [status, setStatus] = useState('pending');
  const [offset, setOffset] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);

  const params = { status: status || undefined, limit: LIMIT, offset };
  const refunds = useStoreRefundRequestsQuery(view === 'refunds' ? store.storeid : null, params);
  const returns = useStoreReturnRequestsQuery(view === 'returns' ? store.storeid : null, params);
  const resolveRefund = useResolveStoreRefundRequest();
  const resolveReturn = useResolveStoreReturnRequest();
  const received = useMarkStoreReturnReceived();
  const busy = resolveRefund.isPending || resolveReturn.isPending || received.isPending;

  const active = view === 'refunds' ? refunds : returns;
  const total = active.data?.total_count ?? 0;
  const code = PERMISSIONS.store.orders;
  const done = () => setPending(null);

  const confirm = (reason: string) => {
    if (!pending) return;
    const base = { storeid: store.storeid, reason };
    if (pending.kind === 'refund')
      resolveRefund.mutate({ ...base, rrid: pending.row.rrid, action: pending.action, seller_notes: reason }, { onSuccess: done });
    else if (pending.kind === 'return')
      resolveReturn.mutate({ ...base, rtid: pending.row.rtid, action: pending.action, seller_notes: reason }, { onSuccess: done });
    else received.mutate({ ...base, rtid: pending.row.rtid, item_condition: pending.condition }, { onSuccess: done });
  };

  const switchView = (v: 'refunds' | 'returns') => {
    setView(v);
    setOffset(0);
    setStatus('pending');
  };

  return (
    <SectionCard
      bare
      description="Requests on this store's orders."
      actions={<StatusFilter options={STATUSES[view]} value={status} onChange={(v) => { setStatus(v); setOffset(0); }} />}
    >
      <div className="mb-3 inline-flex rounded-lg border border-gray-200 p-0.5 text-sm">
        {(['refunds', 'returns'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => switchView(v)}
            className={cn('rounded-md px-3 py-1 font-medium capitalize', view === v ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100')}
          >
            {v}
          </button>
        ))}
      </div>

      {active.isLoading ? (
        <ListSkeleton rows={5} />
      ) : total === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">No {view} {status ? `that are ${label(status)}` : ''}.</p>
      ) : view === 'refunds' ? (
        <div className="divide-y divide-gray-100">
          {(refunds.data?.refund_requests ?? []).map((r) => (
            <div key={r.rrid} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {formatMoney(r.amount_requested)} · order #{r.orderid}
                </p>
                <p className="truncate text-xs text-gray-500">
                  {buyer(r)} · {shortDate(r.date_requested)} · {r.reason || 'No reason given'}
                </p>
                {r.seller_notes && <p className="truncate text-xs text-gray-400">Notes: {r.seller_notes}</p>}
              </div>
              <Pill tone={statusTone(r.status)}>{label(r.status)}</Pill>
              {r.status === 'pending' && (
                <div className="flex gap-2">
                  <PermissionButton code={code} size="sm" onClick={() => setPending({ kind: 'refund', row: r, action: 'approve' })}>Approve</PermissionButton>
                  <PermissionButton code={code} size="sm" variant="outline" onClick={() => setPending({ kind: 'refund', row: r, action: 'reject' })}>Decline</PermissionButton>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {(returns.data?.return_requests ?? []).map((r) => (
            <div key={r.rtid} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Order #{r.orderid} · {label(r.reason)}
                </p>
                <p className="truncate text-xs text-gray-500">
                  {buyer(r)} · {shortDate(r.date_requested)}
                  {r.reason_detail ? ` · ${r.reason_detail}` : ''}
                  {r.item_condition ? ` · item ${r.item_condition}` : ''}
                </p>
              </div>
              <Pill tone={statusTone(r.status)}>{label(r.status)}</Pill>
              {r.status === 'pending' && (
                <div className="flex gap-2">
                  <PermissionButton code={code} size="sm" onClick={() => setPending({ kind: 'return', row: r, action: 'approve' })}>Approve</PermissionButton>
                  <PermissionButton code={code} size="sm" variant="outline" onClick={() => setPending({ kind: 'return', row: r, action: 'reject' })}>Decline</PermissionButton>
                </div>
              )}
              {r.status === 'approved' && (
                <div className="flex gap-2">
                  <PermissionButton code={code} size="sm" variant="outline" onClick={() => setPending({ kind: 'received', row: r, condition: 'resellable' })}>Received · resellable</PermissionButton>
                  <PermissionButton code={code} size="sm" variant="outline" onClick={() => setPending({ kind: 'received', row: r, condition: 'damaged' })}>Received · damaged</PermissionButton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Pager total={total} limit={LIMIT} offset={offset} onChange={setOffset} />

      {pending && (
        <ReasonDialog
          open
          title={
            pending.kind === 'received'
              ? `Return for order #${pending.row.orderid} received (${pending.condition})`
              : `${pending.action === 'approve' ? 'Approve' : 'Decline'} ${pending.kind} for order #${pending.row.orderid}`
          }
          description={
            pending.kind === 'refund' && pending.action === 'approve'
              ? "If the seller has auto-refund on, the amount is debited from the store's wallet now. Issue the gateway refund from Payments."
              : pending.kind === 'received'
                ? 'The order moves to “returned”; a resellable unit goes back into stock.'
                : 'The buyer and the seller see the outcome. Your reason is saved as the seller notes and in the audit log.'
          }
          confirmLabel={pending.kind === 'received' ? 'Mark received' : pending.action === 'approve' ? 'Approve' : 'Decline'}
          destructive={pending.kind !== 'received' && pending.action === 'reject'}
          reasonRequired
          loading={busy}
          onConfirm={confirm}
          onClose={done}
        />
      )}
    </SectionCard>
  );
};
